// Reusable image lightbox — fullscreen zoom + pan + prev/next.
// Zero deps · Pinch/scroll to zoom · Click backdrop to close.
import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';

export default function Lightbox({ images = [], startIndex = 0, onClose, alt = '' }) {
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, images.length - 1));
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0));
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [images.length, onClose]);

  if (!images.length) return null;
  const cur = images[idx];
  return (
    <div className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center" data-testid="lightbox-overlay" onClick={onClose}>
      <button onClick={onClose} data-testid="lightbox-close"
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white">
        <X size={20} />
      </button>
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        <button onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.min(z + 0.5, 4)); }} data-testid="lightbox-zoom-in"
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><ZoomIn size={16} /></button>
        <button onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.max(z - 0.5, 1)); }} data-testid="lightbox-zoom-out"
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><ZoomOut size={16} /></button>
        <span className="px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-black">{idx + 1} / {images.length}</span>
      </div>
      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setIdx(Math.max(idx - 1, 0)); }} disabled={idx === 0} data-testid="lightbox-prev"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30">
            <ChevronLeft size={24} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setIdx(Math.min(idx + 1, images.length - 1)); }} disabled={idx === images.length - 1} data-testid="lightbox-next"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30">
            <ChevronRight size={24} />
          </button>
        </>
      )}
      <img src={cur} alt={alt} data-testid="lightbox-image"
           onClick={(e) => e.stopPropagation()}
           onDoubleClick={() => setZoom((z) => (z >= 2 ? 1 : 2))}
           style={{ transform: `scale(${zoom})`, cursor: zoom > 1 ? 'grab' : 'zoom-in', transition: 'transform 0.2s' }}
           className="max-w-[95vw] max-h-[95vh] object-contain select-none" />
    </div>
  );
}
